import { useState } from "react";
import { CheckCircle2, History, Loader2, TriangleAlert, X } from "lucide-react";
import { RunDetailDialog } from "./RunDetailDialog";
import { formatDateTime } from "../../lib/format";
import { typeRows } from "./reclassifyJob";

const STATUS = { terminado: "Clasificación terminada", error: "La clasificación se detuvo por un error", interrumpido: "La clasificación se interrumpió" };

const Counter = ({ label, value, testId, strong }) => (
  <div className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm ${strong ? "bg-[#eef4ff]" : "bg-[#f5f8fd]"}`} data-testid={testId}>
    <span className="text-[#182235]">{label}</span>
    <span className="font-mono font-bold text-[#0f5ef7]">{value}</span>
  </div>
);

const Counters = ({ job }) => (
  <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3" data-testid="job-counters">
    {typeRows(job.types).map(([label, n, k]) => <Counter key={k} label={label} value={n} testId={`job-type-${k}`} />)}
    <Counter label="Asignados por ID de carpeta" value={job.counts.por_id || 0} testId="job-por-id" strong />
    <Counter label="Asignados a cliente por nombre" value={job.counts.por_nombre || 0} testId="job-por-nombre" strong />
    <Counter label="Asignados por nombre de archivo" value={job.counts.por_archivo || 0} testId="job-por-archivo" strong />
    <Counter label="Se quedan en Por clasificar" value={job.counts.pendientes || 0} testId="job-pendientes" strong />
    <Counter label="Ya existían en el expediente" value={job.counts.ya_existian || 0} testId="job-ya-existian" strong />
  </div>
);

export const JobPanel = ({ job, onDismiss }) => {
  const running = job.status === "en_curso";
  const pct = job.total ? Math.round((job.processed / job.total) * 100) : 0;
  const ok = job.status === "terminado";
  return (
    <section className="tr-card tr-fade-up space-y-3 p-4 sm:p-5" data-testid={running ? "bandeja-job-progress" : "bandeja-job-summary"}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-bold text-[#182235]">
          {running ? <Loader2 className="h-4 w-4 animate-spin text-[#0f5ef7]" /> : ok ? <CheckCircle2 className="h-4 w-4 text-[#067647]" /> : <TriangleAlert className="h-4 w-4 text-[#b42318]" />}
          <span data-testid="job-status-text">{running ? `Clasificando ${job.processed} de ${job.total || "…"} archivos` : STATUS[job.status]}</span>
        </div>
        {running ? <span className="text-xs font-semibold text-[#5b667a]" data-testid="job-remaining">Faltan {Math.max(0, job.total - job.processed)}</span>
          : <button type="button" onClick={onDismiss} className="tr-btn-light px-2.5 py-1.5 text-xs" data-testid="job-dismiss-button"><X className="h-3.5 w-3.5" /> Cerrar resumen</button>}
      </div>
      {running && <>
        <div className="h-2 overflow-hidden rounded-full bg-[#e8edf5]">
          <div className="h-full bg-[#0f5ef7] transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="bandeja-job-progress-bar" />
        </div>
        {job.current_folder && <p className="break-all text-xs text-[#5b667a]" data-testid="job-current-folder">Procesando: <span className="font-mono">{job.current_folder}</span></p>}
      </>}
      {!running && <p className="text-xs text-[#5b667a]" data-testid="job-meta">{job.processed} de {job.total} archivos revisados · {job.started_by} · {formatDateTime(job.finished_at || job.started_at)}</p>}
      <Counters job={job} />
      {running && <p className="text-xs text-[#5b667a]">El proceso corre en el servidor: puedes salir o recargar la página y el avance seguirá aquí.</p>}
    </section>
  );
};

const runCounts = (r) => (r.kind === "repartir"
  ? `clientes eliminados ${r.counts.clientes || 0} · archivados ${r.counts.asignados || 0} · ya existían ${r.counts.ya_existian || 0} · a Por clasificar ${r.counts.por_clasificar || 0}`
  : `clasificados ${r.counts.movidos || 0} · por ID de carpeta ${r.counts.por_id || 0} · por ID y crédito ${r.counts.por_credito || 0} · ya existían ${r.counts.ya_existian || 0} · se quedan ${r.counts.pendientes || 0}`);

// Admin: every reclassify and Repartir run, each with its per-file detail (JRN-130)
export const RunHistory = ({ runs }) => {
  const [open, setOpen] = useState(null);
  return (
    <details className="tr-card p-4 text-sm" data-testid="job-history">
      <summary className="flex cursor-pointer items-center gap-2 font-semibold text-[#182235]" data-testid="job-history-toggle">
        <History className="h-4 w-4 text-[#0f5ef7]" /> Historial de clasificaciones automáticas ({runs.length})
      </summary>
      <ul className="mt-3 space-y-1.5">
        {runs.map((r, i) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#f5f8fd] px-3 py-2 text-xs text-[#5b667a]" data-testid={`run-history-item-${i}`}>
            <span><span className="font-semibold text-[#182235]">{formatDateTime(r.at)}</span> · {r.label} · {r.started_by} · {runCounts(r)}</span>
            <button type="button" onClick={() => setOpen(r)} className="tr-btn-light px-2.5 py-1 text-xs" data-testid={`run-history-detail-${i}`}>Ver detalle</button>
          </li>
        ))}
      </ul>
      <RunDetailDialog run={open} onClose={() => setOpen(null)} />
    </details>
  );
};

export const JobHistory = ({ jobs }) => (
  <details className="tr-card p-4 text-sm" data-testid="job-history">
    <summary className="flex cursor-pointer items-center gap-2 font-semibold text-[#182235]" data-testid="job-history-toggle">
      <History className="h-4 w-4 text-[#0f5ef7]" /> Historial de clasificaciones automáticas ({jobs.length})
    </summary>
    <ul className="mt-3 space-y-1.5">
      {jobs.map((j, i) => (
        <li key={j.id} className="rounded-lg bg-[#f5f8fd] px-3 py-2 text-xs text-[#5b667a]" data-testid={`job-history-item-${i}`}>
          <span className="font-semibold text-[#182235]">{formatDateTime(j.finished_at || j.started_at)}</span> · {j.started_by} · {STATUS[j.status]} ·
          clasificados {j.counts.movidos || 0} · por ID de carpeta {j.counts.por_id || 0} · por nombre {j.counts.por_nombre || 0} · por nombre de archivo {j.counts.por_archivo || 0} · ya existían {j.counts.ya_existian || 0} · se quedan {j.counts.pendientes || 0}
          {typeRows(j.types).length > 0 && <> · {typeRows(j.types).map(([l, n]) => `${l} ${n}`).join(", ")}</>}
        </li>
      ))}
    </ul>
  </details>
);
